import { useCallback, useContext, useState } from 'react';
import { useStatuses } from '@namespace/account';
import { LOG_ACTIONS, logAction, useLogAmplitude } from '@namespace/analytics';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { FORM_FACTORS_MAP, useDeviceStore } from '@namespace/device';
import {
  getGamesByLaunchIds,
  REAL_MODE,
  useGetGameURL
} from '@namespace/games';
import { LOCAL_STORAGE_KEYS, setLocalStorageValue } from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useHistory } from '@namespace/router';
import { UserContext } from '@namespace/user';
import { accept } from '../../api/acceptBonus';
import { acceptWager } from '../../api/freeSpinBonuses';
import { unselectBonusSystemBonus } from '../../api/unselectBonusSystemBonus';
import { bonusTab } from '../../constants';
import { useActiveBsDepositBonusId } from '../../store';

export const useFreeSpinBonusActions = ({
  bonus,
  onAccept,
  onWagerAccept,
  isPlayModeInSearch
}) => {
  const [isRequestProcessing, setIsRequestProcessing] = useState(false);
  const {
    currency,
    gameId,
    serviceId,
    freespinId,
    gameCode,
    isGameManagement,
    isShowWagerButton,
    isNoWallet,
    isPotential,
    isWagerAvailable,
    isCanAccept,
    isReleased,
    isActivation,
    bonusModelId,
    outputMaxTotal,
    outputBetTotal
  } = bonus;
  const { t, language, f } = useI18n();
  const { push } = useHistory();
  const { errorNotification, successNotification } = useNotifications();
  const { isMobile, isTablet } = useDeviceStore();
  const platform =
    isMobile || isTablet ? FORM_FACTORS_MAP.MOBILE : FORM_FACTORS_MAP.DESKTOP;
  const getGameURL = useGetGameURL();
  const {
    deposit,
    casinoWager,
    createWallet: createWalletPage,
    games: casinoPage,
    casinoBonusDetails,
    profilePage
  } = useGeneralRoutes();
  const { isNotVerified, isNeedSetupLimits } = useStatuses();
  const { isBonusSliderCRMBonusClickable = true } = useOtherSettings();
  const activeBonusSystemBonusId = useActiveBsDepositBonusId();
  const DATE_FORMATS_TYPES = f.getFormats();
  const [user] = useContext(UserContext);
  const { registrationDate } = user;
  const logAmplitude = useLogAmplitude();

  const handleAccept = useCallback(async () => {
    try {
      setIsRequestProcessing(true);
      const message = await accept(
        {
          bonus_id: freespinId,
          lang: language,
          platform
        },
        bonusTab.casino
      );
      setIsRequestProcessing(false);
      successNotification(t(message));
      if (onAccept) {
        onAccept(bonus, platform);
      }
      logAmplitude({
        actionName: LOG_ACTIONS.BONUS_ACCEPT_CLICK,
        attributes: {
          bonus_option: 'freespins',
          bonus_template_id: bonusModelId,
          bonus_source: 'crm',
          ...(outputMaxTotal && {
            amount_for_wager: Number(outputMaxTotal)
          }),
          registration_date: f
            .getDateTime({ date: registrationDate?.replace('Z', '') })
            .toFormat(DATE_FORMATS_TYPES.API_WITH_TIME)
        }
      });
    } catch (e) {
      setIsRequestProcessing(false);
      errorNotification(
        t(e.message || 'accountingMessages.accounting_error_6', [currency])
      );
    }
  }, [
    freespinId,
    language,
    platform,
    successNotification,
    t,
    onAccept,
    bonus,
    errorNotification,
    currency
  ]);

  const handleWagerAccept = useCallback(async () => {
    try {
      setIsRequestProcessing(true);
      const message = await acceptWager(bonus, language, platform);
      setIsRequestProcessing(false);
      successNotification(t(message));
      if (onWagerAccept) {
        onWagerAccept(bonus, platform);
      }
    } catch (e) {
      setIsRequestProcessing(false);
      errorNotification(
        e.message ? t(e.message) : t('accountingMessages.accounting_error_6')
      );
    }
  }, [
    bonus,
    language,
    platform,
    successNotification,
    t,
    onWagerAccept,
    errorNotification
  ]);

  const acceptFreeSpin = useCallback(
    async (e) => {
      e.stopPropagation();
      if (isNoWallet) {
        push(createWalletPage);
      } else if (isPotential) {
        if (isBonusSliderCRMBonusClickable) {
          if (activeBonusSystemBonusId) {
            await unselectBonusSystemBonus({
              bonusId: activeBonusSystemBonusId
            });
          }
          setLocalStorageValue(
            LOCAL_STORAGE_KEYS.BONUS_SLIDER_ACTIVE_BONUS,
            freespinId
          );
        }
        logAmplitude({
          actionName: LOG_ACTIONS.DEPOSIT_BUTTON_CLICK,
          attributes: {
            entry_point: 'bonuses',
            verified: !isNotVerified,
            deposit_limits_set: !isNeedSetupLimits,
            bonus_option: 'freespins',
            bonus_template_id: bonusModelId
          },
          isMatchPath: true,
          additionalRoutes: {
            profile: profilePage
          }
        });
        push(deposit);
      } else if (isShowWagerButton) {
        logAmplitude({
          actionName: LOG_ACTIONS.BONUS_WAGER_CLICK,
          attributes: {
            bonus_option: 'freespins',
            bonus_template_id: bonusModelId,
            bonus_source: 'crm',
            ...(outputMaxTotal && {
              amount_for_wager: Number(outputMaxTotal)
            }),
            ...(outputMaxTotal && {
              wagered_percent:
                Number(outputMaxTotal) === 0
                  ? 0
                  : Math.floor((outputBetTotal / Number(outputMaxTotal)) * 100)
            }),
            registration_date: f
              .getDateTime({ date: registrationDate?.replace('Z', '') })
              .toFormat(DATE_FORMATS_TYPES.API_WITH_TIME)
          }
        });
        push(casinoWager);
      } else if (isWagerAvailable) {
        await handleWagerAccept();
      } else if (isCanAccept) {
        await handleAccept();
      } else if (isReleased) {
        let gameIdGM;
        let providerIdGM;

        if (isGameManagement) {
          const {
            games: [{ gameId: launchId }],
            providers: [{ providerId }]
          } = await getGamesByLaunchIds([gameCode], true);

          gameIdGM = launchId;
          providerIdGM = providerId;
        }

        const gameUrl = getGameURL(
          {
            gameCode: gameIdGM || gameId,
            gameProvider: providerIdGM || serviceId,
            playMode: REAL_MODE,
            serviceURL: casinoPage,
            type: 'launch'
          },
          { isPlayModeInSearch }
        );
        logAction(LOG_ACTIONS.FREESPIN_LAUNCH_GAME, {
          gameUrl,
          freespinId,
          platform
        });
        push(gameUrl);
      }
    },
    [
      isNoWallet,
      isPotential,
      isShowWagerButton,
      isWagerAvailable,
      isCanAccept,
      isReleased,
      push,
      createWalletPage,
      isBonusSliderCRMBonusClickable,
      deposit,
      activeBonusSystemBonusId,
      freespinId,
      casinoWager,
      handleWagerAccept,
      handleAccept,
      isGameManagement,
      getGameURL,
      gameId,
      serviceId,
      casinoPage,
      platform,
      gameCode
    ]
  );

  const openDetails = useCallback(
    () =>
      !isRequestProcessing || !isActivation
        ? push(`${casinoBonusDetails}/${freespinId}`)
        : null,
    [isRequestProcessing, isActivation, push, casinoBonusDetails, freespinId]
  );

  return {
    acceptFreeSpin,
    openDetails,
    isRequestProcessing
  };
};
